import { apiPost } from "api/callApi";
import { useEffect, useMemo, useState } from "react";
import { Spin, Button } from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  FullscreenOutlined,
} from "@ant-design/icons";
import theme from "@theme/";

const FileViewer = ({ allDetilsData, fileId }) => {
  const [fileUrl, setFileUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [fileType, setFileType] = useState("");
  const [pdfError, setPdfError] = useState(false);

  const filteredDetailsData = useMemo(() => {
    return allDetilsData.filter((data) => data.id === Number(fileId));
  }, [allDetilsData, fileId]);

  const dataFileUrl = filteredDetailsData[0]?.fileUrl;

  const getUrl = async () => {
    try {
      const response = await apiPost(`/v1/file/downloadfile`, {
        filekey: dataFileUrl,
        onlyUrl: true,
      });
      return response.fileUrl;
    } catch (error) {
      console.log("Error: " + error);
      return;
    }
  };

  useEffect(() => {
    const loadFile = async () => {
      if (!dataFileUrl) return;

      setLoading(true);
      setPdfError(false);
      const url = await getUrl();
      setFileUrl(url);

      const extension = dataFileUrl?.split(".")?.pop()?.toLowerCase();
      setFileType(extension);
      setLoading(false);
    };

    loadFile();
  }, [dataFileUrl]);

  const handlePdfError = () => {
    setPdfError(true);
  };

  const openInNewTab = () => {
    window.open(fileUrl, "_blank");
  };

  const renderPreview = () => {
    return (
      <>
        {fileType === "pdf" && !pdfError && (
          <div style={styles.pdfContainer}>
            <object
              data={fileUrl}
              type="application/pdf"
              width="100%"
              height="650px"
              style={styles.iframe}
              onError={handlePdfError}
            >
              <embed
                src={fileUrl}
                type="application/pdf"
                width="100%"
                height="650px"
                style={styles.iframe}
                onError={handlePdfError}
              />
            </object>
          </div>
        )}

        {fileType === "pdf" && pdfError && (
          <div style={styles.pdfFallback}>
            <p style={styles.fallbackText}>
              PDF preview is not available in your browser
            </p>
            <div style={styles.buttonGroup}>
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={openInNewTab}
                style={{
                  backgroundColor: theme.colors.themeColor,
                  color: theme.colors.white,
                  marginRight: "10px",
                }}
              >
                Open in New Tab
              </Button>
              <Button
                icon={<DownloadOutlined />}
                href={fileUrl}
                download
                style={{
                  borderColor: theme.colors.themeColor,
                  color: theme.colors.themeColor,
                }}
              >
                Download PDF
              </Button>
            </div>
          </div>
        )}

        {["jpg", "jpeg", "png", "webp"].includes(fileType) && (
          <img src={fileUrl} alt="Preview" style={styles.image} />
        )}

        {!["pdf", "jpg", "jpeg", "png", "webp"].includes(fileType) && (
          <div style={styles.unsupported}>
            <p>Preview not supported for this file type</p>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              href={fileUrl}
              download
              style={{
                backgroundColor: theme.colors.themeColor,
                color: theme.colors.white,
              }}
            >
              Download File
            </Button>
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div style={styles.loaderWrap}>
        <Spin size="large" tip="Loading file preview..." />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.previewBox}>
        {fileUrl && ["pdf", "jpg", "jpeg", "png", "webp"].includes(fileType) && (
          <div style={styles.previewActions}>
            <Button
              icon={<FullscreenOutlined />}
              onClick={openInNewTab}
              style={{
                borderColor: theme.colors.themeColor,
                color: theme.colors.themeColor,
              }}
            >
              Full View
            </Button>
          </div>
        )}

        {renderPreview()}
      </div>
    </div>
  );
};

const styles = {
  container: {
    borderRadius: "12px",
    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
    marginTop: "10px",
  },
  previewBox: {
    position: "relative",
    border: "1px solid #f0f0f0",
    borderRadius: "10px",
    padding: "10px",
    background: "#fafafa",
    textAlign: "center",
  },
  previewActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "10px",
  },
  pdfContainer: {
    width: "100%",
    height: "650px",
  },
  iframe: {
    border: "none",
    borderRadius: "8px",
    background: "#fff",
  },
  image: {
    maxWidth: "100%",
    maxHeight: "650px",
    objectFit: "contain",
    borderRadius: "10px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
  },
  pdfFallback: {
    padding: "60px 20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },
  fallbackText: {
    fontSize: "16px",
    color: "#666",
    marginBottom: "24px",
  },
  buttonGroup: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  unsupported: {
    paddingBottom: "4px",
    color: "#999",
    padding: "60px 20px",
  },
  loaderWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "180px",
  },
};

export default FileViewer;
